import { UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import { fpDivide, toNumber } from '@/helpers/unit-formatter/unit-formatter.utils';

import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Transforms frequency values from Hertz to other frequency units.
 * Supports conversion to kilohertz (kHz), megahertz (MHz), and gigahertz (GHz).
 * Uses floating-point safe arithmetic operations for accurate conversions.
 *
 * @param {UnitValue} unitValue - The frequency value in Hertz
 * @param {any} unitValue.value - The numeric frequency value in Hertz
 * @param {string} [unitValue.units] - The current units (should be 'Hz' or similar)
 * @param {string} [newUnits] - Target frequency unit ('kHz', 'MHz', 'GHz')
 * @returns {UnitValue} Transformed frequency value in the target units
 *   - Returns original value if newUnits is not recognized
 *   - Returns original value if input value is null/undefined
 *
 * @example
 * transformHz({ value: 1000, units: 'Hz' }, 'kHz')
 * // Returns: { value: 1, units: 'kHz' }
 *
 * @example
 * transformHz({ value: 1500000, units: 'Hz' }, 'MHz')
 * // Returns: { value: 1.5, units: 'MHz' }
 *
 * @example
 * transformHz({ value: 2400000000, units: 'Hz' }, 'GHz')
 * // Returns: { value: 2.4, units: 'GHz' }
 */
export const transformHz = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  const value = toNumber(unitValue.value);
  if (value == null || value == undefined) return unitValue;

  switch (newUnits?.toLowerCase()) {
    case SUPPORTED_UNITS.KHZ:
      return {
        value: fpDivide(value, 1000),
        units: newUnits,
      };
    case SUPPORTED_UNITS.MHZ:
      return {
        value: fpDivide(value, 1000000),
        units: newUnits,
      };
    case SUPPORTED_UNITS.GHZ:
      return {
        value: fpDivide(value, 1000000000),
        units: newUnits,
      };
    default:
      return unitValue;
  }
};
